import { useState, useEffect, useCallback, useMemo } from 'react';
import { Zap, Plus, Search, ChevronLeft, ChevronRight, Wifi, WifiOff, Power, Tag, Trash2, Building2, Globe, Home, Radio } from 'lucide-react';
import {
  fetchChargers, fetchChargerOversight, fetchTariffs, bulkChargerAction,
  type Charger, type ChargerOversightRow, type Tariff,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import {
  QUERY_CHANGE_EVENT, canChooseScope, chargerListRequest, latestRequestGate, readScope, rememberChargersList, scopeSearch,
  vendorModelLabel, type ChargerScope,
} from '@/lib/chargerScope';
import { Modal } from '@/components/ui/Modal';
import { AddChargerForm } from '@/components/ui/AddChargerForm';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { FilterSheet } from '@/components/ui/FilterSheet';
import { useIsMobile } from '@/hooks/useViewport';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useLiveReload } from '@/components/LiveUpdatesProvider';
import { useStartTimeouts } from '@/hooks/useStartTimeouts';
import { TimeoutCell } from '@/components/ui/TimeoutCell';
import type { StartTimeoutChargerRow } from '@evi/shared';
import styles from './ChargersPage.module.css';

interface ChargersPageProps {
  onNavigate: (path: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  Available: '#2ecc71',
  Charging: '#3498db',
  Preparing: '#f39c12',
  Finishing: '#9b59b6',
  Faulted: '#e74c3c',
  Unavailable: '#95a5a6',
  Reserved: '#e67e22',
  Occupied: '#3498db',
  SuspendedEV: '#f39c12',
  SuspendedEVSE: '#f39c12',
};

export function ChargersPage({ onNavigate }: ChargersPageProps) {
  useRecentRoute('Chargers');
  const isMobile = useIsMobile();
  // Two-Minute Window 4.5 — app-start timeout rate per charger (30d),
  // one shared read for the whole table; rows join by charger _id.
  const timeoutsQ = useStartTimeouts('30d');
  const timeoutByCharger = new Map<string, StartTimeoutChargerRow>(
    (timeoutsQ.data?.chargers ?? []).map((r) => [r.charger_id, r]),
  );
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterOnline, setFilterOnline] = useState<boolean | undefined>();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [showTariffPicker, setShowTariffPicker] = useState(false);

  // One Chargers page (Warren, 28 October 2026; lib/chargerScope.ts). The
  // Super Admin chooses EVI's own or the whole network, carried in
  // ?scope=network, and reads the oversight list with its operator, site,
  // type, Stale state and firmware, beside the Vendor / Model, OCPP and
  // timeouts the Chargers table always had. Everyone else keeps exactly the page
  // below, on GET /chargers.
  const user = useAuthStore((s) => s.user);
  const scopeUser = useMemo(() => ({ role: user?.role, tenant_id: user?.tenant_id }), [user?.role, user?.tenant_id]);
  const scoped = canChooseScope(scopeUser);
  const [scope, setScope] = useState<ChargerScope>(() => readScope(window.location.search, scopeUser));
  const [rows, setRows] = useState<ChargerOversightRow[]>([]);
  const [offlineOnly, setOfflineOnly] = useState(false);
  const [residentialOnly, setResidentialOnly] = useState(false);

  const applyScope = useCallback((next: ChargerScope) => {
    setScope(next);
    setPage(1);
    setSelected(new Set());
  }, []);
  // replaceState, the MoneyPage way: switching scope does not bury the page
  // the user came from under history entries.
  const chooseScope = (next: ChargerScope) => {
    if (next === scope) return;
    applyScope(next);
    window.history.replaceState({}, '', `${window.location.pathname}${scopeSearch(window.location.search, next)}`);
  };
  // Back, forward, and a menu link to the other scope while on this page
  // (App.tsx announces that query-only move to this page alone).
  useEffect(() => {
    const onUrl = () => {
      const next = readScope(window.location.search, scopeUser);
      if (next !== scope) applyScope(next);
    };
    window.addEventListener('popstate', onUrl);
    window.addEventListener(QUERY_CHANGE_EVENT, onUrl);
    return () => {
      window.removeEventListener('popstate', onUrl);
      window.removeEventListener(QUERY_CHANGE_EVENT, onUrl);
    };
  }, [scope, scopeUser, applyScope]);
  // Back to Chargers on a charger's own pages returns to this list, its
  // scope kept (lib/chargerScope.ts). The URL is already the new one here.
  useEffect(() => {
    rememberChargersList(`${window.location.pathname}${window.location.search}`);
  }, [scope]);
  const listIds = scoped ? rows.map((r) => r.id) : chargers.map((c) => c._id);

  // Bulk selection is a desktop-only affordance. At narrow widths
  // the checkbox column + bulk toolbar would crowd out the charger
  // info, and the expected flow on mobile is "tap a charger to
  // manage it individually." Clear any stray selection when the
  // viewport flips, so the bulk toolbar doesn't stay pinned under
  // an invisible checkbox.
  useEffect(() => {
    if (isMobile && selected.size > 0) setSelected(new Set());
  }, [isMobile, selected.size]);

  // Load tariffs once for the bulk dropdown
  useEffect(() => {
    fetchTariffs().then(setTariffs).catch(() => null);
  }, []);

  const toggleAll = () => {
    // While a new list loads, the rows behind the header checkbox are the
    // old request's; select all only what is on screen.
    if (loading) return;
    if (selected.size === listIds.length) setSelected(new Set());
    else setSelected(new Set(listIds));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: 'activate' | 'deactivate' | 'delete', extra?: { tariff_id?: string }) => {
    if (selected.size === 0) return;
    if (action === 'delete' && !confirm(`Deactivate ${selected.size} charger(s)?`)) return;

    setBulkBusy(true);
    try {
      const result = await bulkChargerAction({
        action,
        charger_ids: Array.from(selected),
        ...extra,
      });
      alert(result.message + ` (${result.modified}/${result.matched})`);
      setSelected(new Set());
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const runAssignTariff = async (tariffId: string) => {
    if (selected.size === 0) return;
    setShowTariffPicker(false);
    setBulkBusy(true);
    try {
      const result = await bulkChargerAction({
        action: 'assign_tariff',
        charger_ids: Array.from(selected),
        tariff_id: tariffId,
      });
      alert(result.message + ` (${result.modified}/${result.matched})`);
      setSelected(new Set());
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  // Only the newest request fills the page (lib/chargerScope.ts): a late
  // reply for an earlier scope, page, search or filter is dropped, on both
  // lists, so the rows shown are the ones the bulk actions act on.
  // `silent` (live reloads) refetches in place: no spinner, and a failed
  // background refetch keeps the rows already shown.
  const [requests] = useState(latestRequestGate);
  const load = useCallback(async (silent = false) => {
    const ticket = requests.begin();
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const request = chargerListRequest(scopeUser, scope, { page, search, isOnline: filterOnline, offlineOnly, residentialOnly });
      if (request.api === 'oversight') {
        const res = await fetchChargerOversight(request.params);
        if (!requests.isLatest(ticket)) return;
        setRows(res.data);
        setTotalPages(res.meta.pages);
        setTotal(res.meta.total);
      } else {
        const res = await fetchChargers(request.params);
        if (!requests.isLatest(ticket)) return;
        setChargers(res.data);
        setTotalPages(res.meta.pages);
        setTotal(res.meta.total);
      }
    } catch (err) {
      // A failed live reload keeps the rows already shown, without an error.
      if (silent) return;
      if (requests.isLatest(ticket)) setError(err instanceof Error ? err.message : 'Failed to load chargers');
    } finally {
      // The latest request ends the spinner, silent or not.
      if (requests.isLatest(ticket)) setLoading(false);
    }
  }, [requests, scopeUser, scope, page, search, filterOnline, offlineOnly, residentialOnly]);

  useEffect(() => { load(); }, [load]);

  // Live (28 Oct 2026): status, online and connector changes refresh the
  // list in place; any charger, since a change can move one onto this page.
  useLiveReload({ kinds: ['charger'], reload: () => { void load(true); } });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Zap size={18} />
          </div>
          <div>
            <h1 className={styles.title}>Chargers</h1>
            <p className={styles.subtitle}>
              {total} charger{total !== 1 ? 's' : ''} {scoped && scope === 'network' ? 'across every operator, residential included' : 'registered'}
            </p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={() => setAddModalOpen(true)}>
          <Plus size={16} />
          Add Charger
        </button>
      </div>

      {/* The Super Admin's scope, above search and on a phone too. */}
      {scoped && (
        <div className={styles.filterBtns} role="group" aria-label="Which chargers">
          <button
            type="button"
            className={`${styles.filterBtn} ${scope === 'own' ? styles.filterActive : ''}`}
            aria-pressed={scope === 'own'}
            onClick={() => chooseScope('own')}
          >
            <Building2 size={14} /> EVI&apos;s own
          </button>
          <button
            type="button"
            className={`${styles.filterBtn} ${scope === 'network' ? styles.filterActive : ''}`}
            aria-pressed={scope === 'network'}
            onClick={() => chooseScope('network')}
          >
            <Globe size={14} /> Whole network
          </button>
        </div>
      )}

      {/* Filters — search bar stays inline; the three online/offline
          toggles collapse into a FilterSheet on mobile so the toolbar
          doesn't overflow at 375px. */}
      <div className={styles.filters}>
        <form className={styles.searchBar} onSubmit={handleSearch}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by identity, vendor, model..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </form>
        {/* The oversight list's own two filters for the Super Admin. */}
        {scoped ? (
          <div className={styles.filterBtns}>
            <button
              type="button"
              className={`${styles.filterBtn} ${offlineOnly ? styles.filterActive : ''}`}
              aria-pressed={offlineOnly}
              onClick={() => { setOfflineOnly((v) => !v); setPage(1); }}
            >
              <WifiOff size={14} /> Offline only
            </button>
            <button
              type="button"
              className={`${styles.filterBtn} ${residentialOnly ? styles.filterActive : ''}`}
              aria-pressed={residentialOnly}
              onClick={() => { setResidentialOnly((v) => !v); setPage(1); }}
            >
              <Home size={14} /> Residential only
            </button>
          </div>
        ) : (
        <FilterSheet
          triggerLabel="Status"
          activeCount={filterOnline === undefined ? 0 : 1}
        >
          <div className={styles.filterBtns}>
            <button
              className={`${styles.filterBtn} ${filterOnline === undefined ? styles.filterActive : ''}`}
              onClick={() => { setFilterOnline(undefined); setPage(1); }}
            >
              All
            </button>
            <button
              className={`${styles.filterBtn} ${filterOnline === true ? styles.filterActive : ''}`}
              onClick={() => { setFilterOnline(true); setPage(1); }}
            >
              <Wifi size={14} /> Online
            </button>
            <button
              className={`${styles.filterBtn} ${filterOnline === false ? styles.filterActive : ''}`}
              onClick={() => { setFilterOnline(false); setPage(1); }}
            >
              <WifiOff size={14} /> Offline
            </button>
          </div>
        </FilterSheet>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Bulk action toolbar — desktop-only (mobile strips selection) */}
      {!isMobile && selected.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selected.size} selected</span>
          <div className={styles.bulkActions}>
            <button onClick={() => runBulk('activate')} disabled={bulkBusy}>
              <Power size={14} /> Activate
            </button>
            <button onClick={() => runBulk('deactivate')} disabled={bulkBusy}>
              <Power size={14} /> Deactivate
            </button>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowTariffPicker((v) => !v)} disabled={bulkBusy}>
                <Tag size={14} /> Assign Tariff
              </button>
              {showTariffPicker && (
                <div className={styles.tariffPicker}>
                  {tariffs.length === 0 ? (
                    <div className={styles.tariffEmpty}>No tariffs available</div>
                  ) : (
                    tariffs.map((t) => (
                      <button key={t._id} onClick={() => runAssignTariff(t._id)} className={styles.tariffPickItem}>
                        {t.name} <span className={styles.tariffRate}>{t.rates.energy_rate.toFixed(2)}¢/kWh</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button onClick={() => runBulk('delete')} disabled={bulkBusy} className={styles.bulkDanger}>
              <Trash2 size={14} /> Delete
            </button>
            <button onClick={() => setSelected(new Set())} className={styles.bulkCancel}>Cancel</button>
          </div>
        </div>
      )}

      {/* Mobile: stacked cards via ResponsiveTable. The desktop path
          below keeps its own hand-rolled table because the bulk-select
          checkbox column needs nested-click + indeterminate state that
          the generic primitive doesn't model. */}
      {scoped ? (
        <ScopedChargerList
          rows={rows}
          loading={loading}
          isMobile={isMobile}
          timeoutByCharger={timeoutByCharger}
          selected={selected}
          onToggleAll={toggleAll}
          onToggleOne={toggleOne}
          onOpen={(id) => onNavigate(`/chargers/${id}`)}
        />
      ) : isMobile ? (
        <MobileChargerList
          chargers={chargers}
          loading={loading}
          timeoutByCharger={timeoutByCharger}
          onOpen={(ch) => onNavigate(`/chargers/${ch._id}`)}
        />
      ) : (
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === chargers.length}
                  ref={(input) => {
                    if (input) input.indeterminate = selected.size > 0 && selected.size < chargers.length;
                  }}
                  onChange={toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              <th>Identity</th>
              <th>Vendor / Model</th>
              <th>OCPP</th>
              <th>Connectors</th>
              <th>Status</th>
              <th title="App starts the charger accepted where nobody plugged in before its plug-in window closed. Flagged above 40% of 10+ starts.">Timeouts (30d)</th>
              <th>Last Heartbeat</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className={styles.loading}>Loading...</td></tr>
            ) : chargers.length === 0 ? (
              <tr><td colSpan={8} className={styles.empty}>No chargers found</td></tr>
            ) : (
              chargers.map((ch) => (
                <tr
                  key={ch._id}
                  className={styles.row}
                  onClick={() => onNavigate(`/chargers/${ch._id}`)}
                >
                  <td onClick={(e) => { e.stopPropagation(); toggleOne(ch._id); }}>
                    <input
                      type="checkbox"
                      checked={selected.has(ch._id)}
                      onChange={() => toggleOne(ch._id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className={styles.identity}>{ch.identity}</td>
                  <td>{ch.vendor} {ch.model}</td>
                  <td><span className={styles.ocppBadge}>{ch.ocpp_version}</span></td>
                  <td>
                    <div className={styles.connectorDots}>
                      {ch.connectors.map((c) => (
                        <span
                          key={c.connector_id}
                          className={styles.connectorDot}
                          style={{ background: STATUS_COLORS[c.status] ?? '#95a5a6' }}
                          title={`#${c.connector_id}: ${c.status} (${c.type})`}
                        />
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.onlineBadge} ${ch.is_online ? styles.online : styles.offline}`}>
                      {ch.is_online ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td><TimeoutCell row={timeoutByCharger.get(ch._id)} /></td>
                  <td className={styles.dimText}>
                    {ch.last_heartbeat
                      ? new Date(ch.last_heartbeat).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Register Charger"
        size="lg"
      >
        <AddChargerForm
          onSuccess={() => { setAddModalOpen(false); setPage(1); load(); }}
          onCancel={() => setAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

// ── Mobile card list — scoped to ChargersPage ────────────────────────
function MobileChargerList({
  chargers, loading, timeoutByCharger, onOpen,
}: {
  chargers: Charger[];
  loading: boolean;
  timeoutByCharger: Map<string, StartTimeoutChargerRow>;
  onOpen: (charger: Charger) => void;
}) {
  const columns: Column<Charger>[] = [
    {
      key: 'identity',
      header: 'Identity',
      mobile: 'primary',
      cell: (ch) => ch.identity,
    },
    {
      key: 'vendorModel',
      header: 'Vendor / Model',
      mobile: 'secondary',
      cell: (ch) => `${ch.vendor} ${ch.model}`,
    },
    {
      key: 'ocpp',
      header: 'OCPP',
      mobile: 'body',
      cell: (ch) => (
        <span className={styles.ocppBadge}>{ch.ocpp_version}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (ch) => (
        <span className={`${styles.onlineBadge} ${ch.is_online ? styles.online : styles.offline}`}>
          {ch.is_online ? 'Online' : 'Offline'}
        </span>
      ),
    },
    {
      key: 'connectors',
      header: 'Connectors',
      mobile: 'body',
      cell: (ch) => (
        <span className={styles.connectorDots}>
          {ch.connectors.map((c) => (
            <span
              key={c.connector_id}
              className={styles.connectorDot}
              style={{ background: STATUS_COLORS[c.status] ?? '#95a5a6' }}
              title={`#${c.connector_id}: ${c.status} (${c.type})`}
            />
          ))}
        </span>
      ),
    },
    {
      key: 'timeouts',
      header: 'Timeouts (30d)',
      mobile: 'body',
      cell: (ch) => <TimeoutCell row={timeoutByCharger.get(ch._id)} />,
    },
    {
      key: 'heartbeat',
      header: 'Last seen',
      mobile: 'body',
      cell: (ch) => ch.last_heartbeat
        ? new Date(ch.last_heartbeat).toLocaleString()
        : '—',
    },
  ];

  if (loading) return <p className={styles.loading}>Loading...</p>;
  return (
    <ResponsiveTable
      columns={columns}
      rows={chargers}
      rowKey={(ch) => ch._id}
      onRowClick={onOpen}
      emptyMessage="No chargers found"
    />
  );
}

// ── The Super Admin's list, both scopes (the old Network Chargers page's columns, and Chargers' own) ──
// Problems first, as the oversight endpoint sorts: offline before online,
// then the oldest heartbeat. Owner over operator for a resident's charger.
const SCOPE_OFFLINE = { label: 'Offline', bg: 'rgba(231,76,60,0.16)', fg: '#e74c3c' };
const SCOPE_STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  online: { label: 'Online', bg: 'rgba(46,204,113,0.16)', fg: '#2ecc71' },
  stale: { label: 'Stale', bg: 'rgba(243,156,18,0.16)', fg: '#f39c12' },
  offline: SCOPE_OFFLINE,
};

const SCOPE_TYPE_LABEL: Record<string, string> = {
  residential: 'Residential',
  multifamily: 'Multifamily',
  commercial: 'Commercial',
  public: 'Public',
  fleet: 'Fleet',
  unknown: 'Unknown',
};

function heartbeatAge(row: ChargerOversightRow): string {
  if (!row.last_heartbeat) return 'Never';
  const mins = row.heartbeat_age_minutes ?? 0;
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / (60 * 24))} d ago`;
}

function ScopeIdentity({ row }: { row: ChargerOversightRow }) {
  return (
    <>
      {row.display_name || row.identity}
      {row.display_name && <div className={styles.dimText} style={{ fontSize: 11 }}>{row.identity}</div>}
    </>
  );
}

function ScopeOwner({ row }: { row: ChargerOversightRow }) {
  return (
    <>
      {row.owner ? (row.owner.name || row.owner.email || 'Resident') : row.tenant.name}
      {row.owner && <div className={styles.dimText} style={{ fontSize: 11 }}>{row.tenant.name}</div>}
    </>
  );
}

function ScopeType({ row }: { row: ChargerOversightRow }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {row.charger_type === 'residential' && <Home size={12} />}
      {row.sim_provider === '1nce' && <Radio size={12} aria-label="cellular" />}
      {SCOPE_TYPE_LABEL[row.charger_type] ?? row.charger_type}
    </span>
  );
}

function ScopeOcpp({ row }: { row: ChargerOversightRow }) {
  return row.ocpp_version
    ? <span className={styles.ocppBadge}>{row.ocpp_version}</span>
    : <span className={styles.dimText}>Unknown</span>;
}

function ScopeStatus({ row }: { row: ChargerOversightRow }) {
  const st = SCOPE_STATUS[row.status] ?? SCOPE_OFFLINE;
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 999,
        fontSize: 11, fontWeight: 700, background: st.bg, color: st.fg,
        // One line, icon and label together, in the wider eleven-column table.
        whiteSpace: 'nowrap',
      }}
      title={
        row.status === 'stale'
          ? 'Flagged online but no heartbeat in 15+ minutes'
          : row.live_connected && !row.is_online
            ? 'Live websocket open but DB flag lags'
            : undefined
      }
    >
      {row.is_online
        ? <Wifi size={11} style={{ verticalAlign: -1, marginRight: 3 }} />
        : <WifiOff size={11} style={{ verticalAlign: -1, marginRight: 3 }} />}
      {st.label}
      {row.live_connected && !row.is_online ? ' · live' : ''}
    </span>
  );
}

// The oversight columns, with the Vendor / Model, OCPP and 30-day timeouts
// the Super Admin's Chargers table always had (timeouts join by charger id).
function ScopedChargerList({
  rows, loading, isMobile, timeoutByCharger, selected, onToggleAll, onToggleOne, onOpen,
}: {
  rows: ChargerOversightRow[];
  loading: boolean;
  isMobile: boolean;
  timeoutByCharger: Map<string, StartTimeoutChargerRow>;
  selected: Set<string>;
  onToggleAll: () => void;
  onToggleOne: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  if (isMobile) {
    // Cards on a phone; selection, like the bulk bar, is desktop only.
    const columns: Column<ChargerOversightRow>[] = [
      { key: 'identity', header: 'Identity', mobile: 'primary', cell: (r) => <ScopeIdentity row={r} /> },
      { key: 'owner', header: 'Owner / Operator', mobile: 'secondary', cell: (r) => <ScopeOwner row={r} /> },
      { key: 'location', header: 'Location', mobile: 'body', cell: (r) => r.location.name },
      { key: 'type', header: 'Type', mobile: 'body', cell: (r) => <ScopeType row={r} /> },
      { key: 'vendorModel', header: 'Vendor / Model', mobile: 'body', cell: (r) => vendorModelLabel(r) },
      { key: 'ocpp', header: 'OCPP', mobile: 'body', cell: (r) => <ScopeOcpp row={r} /> },
      { key: 'status', header: 'Status', mobile: 'body', cell: (r) => <ScopeStatus row={r} /> },
      { key: 'timeouts', header: 'Timeouts (30d)', mobile: 'body', cell: (r) => <TimeoutCell row={timeoutByCharger.get(r.id)} /> },
      { key: 'heartbeat', header: 'Last seen', mobile: 'body', cell: (r) => heartbeatAge(r) },
      { key: 'firmware', header: 'Firmware', mobile: 'body', cell: (r) => r.firmware_version ?? 'Unknown' },
    ];
    if (loading) return <p className={styles.loading}>Loading...</p>;
    return (
      <ResponsiveTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => onOpen(r.id)}
        emptyMessage="No chargers found"
      />
    );
  }
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                checked={selected.size > 0 && selected.size === rows.length}
                ref={(input) => {
                  if (input) input.indeterminate = selected.size > 0 && selected.size < rows.length;
                }}
                onChange={onToggleAll}
                onClick={(e) => e.stopPropagation()}
              />
            </th>
            <th>Identity</th>
            <th>Owner / Operator</th>
            <th>Location</th>
            <th>Type</th>
            <th>Vendor / Model</th>
            <th>OCPP</th>
            <th>Status</th>
            <th title="App starts the charger accepted where nobody plugged in before its plug-in window closed. Flagged above 40% of 10+ starts.">Timeouts (30d)</th>
            <th>Last Heartbeat</th>
            <th>Firmware</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={11} className={styles.loading}>Loading...</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={11} className={styles.empty}>No chargers found</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id} className={styles.row} onClick={() => onOpen(r.id)}>
                <td onClick={(e) => { e.stopPropagation(); onToggleOne(r.id); }}>
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => onToggleOne(r.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={styles.identity}><ScopeIdentity row={r} /></td>
                <td><ScopeOwner row={r} /></td>
                <td>{r.location.name}</td>
                <td><ScopeType row={r} /></td>
                <td>{vendorModelLabel(r)}</td>
                <td><ScopeOcpp row={r} /></td>
                <td><ScopeStatus row={r} /></td>
                <td><TimeoutCell row={timeoutByCharger.get(r.id)} /></td>
                <td className={styles.dimText}>{heartbeatAge(r)}</td>
                <td className={styles.dimText}>{r.firmware_version ?? 'Unknown'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
